import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Star, ExternalLink, Download } from "lucide-react";
import { api, assetUrl, formatApiError, API, isPlaceholder } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UsersTab } from "@/components/admin/UsersTab";
import { ExecutiveReviewTab } from "@/components/admin/ExecutiveReviewTab";
import { VideosTab } from "@/components/admin/VideosTab";

const ConfigWarning = ({ eds }) => {
  const missing = eds.flatMap((e) => [["Legal URL", e.legal_url], ["Privacy URL", e.privacy_url], ["Support email", e.support_email]].filter(([, v]) => isPlaceholder(v)).map(([k]) => `${e.brand_name}: ${k}`));
  if (!missing.length) return null;
  return (
    <div data-testid="admin-config-warning" className="mb-6 border-l-4 border-brand-action bg-card p-4 text-sm">
      <div className="font-semibold">Required configuration missing</div>
      <p className="mt-1 text-muted-foreground">These are still placeholders and are hidden from the footer and sign-in page until set (open the edition to edit): {missing.join(" · ")}</p>
    </div>
  );
};

const NewEdition = () => {
  const navigate = useNavigate();
  const [f, setF] = useState({ slug: "", brand_name: "" });
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/editions", { ...f, edition_label: `The ${f.brand_name} Masterclass`, certificate_issuer: f.brand_name, partner_name: f.brand_name });
      navigate(`/admin/editions/${data.slug}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog>
      <DialogTrigger data-testid="admin-new-edition-button" className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-fg"><Plus className="h-4 w-4" /> New edition</DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>Create a partner edition from the core course</DialogTitle></DialogHeader>
        <form onSubmit={create} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="ne-brand">Brand name</Label><Input id="ne-brand" data-testid="new-edition-brand-input" required value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} /></div>
          <div className="space-y-2"><Label htmlFor="ne-slug">Link slug (/e/slug)</Label><Input id="ne-slug" data-testid="new-edition-slug-input" required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })} /></div>
          <button data-testid="new-edition-submit-button" className="rounded-sm bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-fg">Create</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditionsTab = () => {
  const qc = useQueryClient();
  const { slug: activeSlug, chooseBrand, reload } = useBrand();
  const { data: eds } = useQuery({ queryKey: ["admin-editions"], queryFn: async () => (await api.get("/admin/editions")).data });
  if (!eds) return <Loader />;
  const makeDefault = async (slug) => {
    await api.post(`/admin/editions/${slug}/default`);
    await qc.invalidateQueries({ queryKey: ["admin-editions"] });
    chooseBrand(null);
    await reload();
    toast.success("Default brand updated for the main site");
  };
  return (
    <div>
      <ConfigWarning eds={eds} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">This browser is showing: <strong data-testid="admin-active-brand">{activeSlug}</strong>{" "}
          <button data-testid="admin-reset-brand-button" onClick={() => chooseBrand(null)} className="text-brand-primary underline">use main-site default</button></p>
        <NewEdition />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {eds.map((e) => (
          <div key={e.slug} data-testid={`admin-edition-${e.slug}`} className="flex flex-col border border-border bg-card p-6" style={{ borderTop: `4px solid ${e.colors?.primary}` }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex h-12 items-center rounded-sm bg-white px-2">{e.logo_url ? <img src={assetUrl(e.logo_url)} alt={`${e.brand_name} logo`} className="h-9 max-w-[180px] object-contain" /> : <span className="font-bold text-neutral-900">{e.brand_name}</span>}</div>
              <div className="flex gap-2 text-xs">
                {e.is_default && <span data-testid={`edition-default-badge-${e.slug}`} className="rounded-sm bg-brand-action px-2 py-0.5 font-semibold text-brand-action-fg">Default</span>}
                <span className="rounded-sm border border-border px-2 py-0.5 uppercase">{e.status}</span>
              </div>
            </div>
            <div className="mt-4 font-heading text-lg font-bold">{e.edition_label}</div>
            <div className="font-mono text-xs text-muted-foreground">/e/{e.slug} · v{e.edition_version}</div>
            <div className="mt-3 flex gap-1">{["primary", "accent", "data", "action"].map((k) => <span key={k} title={k} className="h-5 w-8 border border-border" style={{ background: e.colors?.[k] }} />)}</div>
            <div className="mt-6 flex flex-wrap gap-2 text-sm">
              <Link to={`/admin/editions/${e.slug}`} data-testid={`edit-edition-${e.slug}`} className="rounded-sm border border-border px-3 py-1.5 font-semibold hover:bg-muted">Edit brand & theme</Link>
              {!e.is_default && <button onClick={() => makeDefault(e.slug)} data-testid={`set-default-${e.slug}`} className="inline-flex items-center gap-1 rounded-sm border border-border px-3 py-1.5 font-semibold hover:bg-muted"><Star className="h-3.5 w-3.5" /> Set as default</button>}
              <a href={`/e/${e.slug}`} data-testid={`open-edition-${e.slug}`} className="inline-flex items-center gap-1 rounded-sm border border-border px-3 py-1.5 font-semibold hover:bg-muted"><ExternalLink className="h-3.5 w-3.5" /> Open /e/{e.slug}</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LearnersTab = () => {
  const { data } = useQuery({ queryKey: ["admin-learners"], queryFn: async () => (await api.get("/admin/learners")).data });
  if (!data) return <Loader />;
  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table data-testid="admin-learners-table" className="w-full text-left text-sm">
        <thead><tr className="border-b border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{["Learner", "Edition", "Placement", "Current tier", "Foundation", "Practitioner", "Strategist"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.id} className="border-b border-border">
              <td translate="no" className="p-3"><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
              <td className="p-3">{r.edition_slug}</td><td className="p-3">{r.placement_level || "—"}</td><td className="p-3">{r.current_tier}</td>
              {["foundation", "practitioner", "strategist"].map((t) => <td key={t} className="p-3 text-xs">{r.tiers[t] ? `${r.tiers[t].status} · ${r.tiers[t].passed_modules}/8` : "—"}</td>)}
            </tr>
          ))}
          {!data.length && <tr><td className="p-6 text-muted-foreground" colSpan={7}>No learners yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const AnalyticsTab = () => {
  const { data } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const exportCsv = async () => {
    const res = await api.get("/admin/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "anonymised-completions.csv";
    a.click();
  };
  if (!data) return <Loader />;
  return (
    <div>
      <button onClick={exportCsv} data-testid="admin-export-button" title={`${API}/admin/export`} className="mb-6 inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"><Download className="h-4 w-4" /> Export anonymised completions (CSV)</button>
      <div className="grid gap-4 md:grid-cols-2">
        {data.map((s) => (
          <div key={s.slug} data-testid={`admin-stats-${s.slug}`} className="border border-border bg-card p-6">
            <div className="font-heading text-lg font-bold">{s.brand_name}</div>
            <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {[["Learners", s.learners], ["Certified attempts", s.certified_attempts], ["Avg certified score", s.avg_certified_score ?? "—"], ["Capstone submissions", s.capstones], ["CTA clicks", s.cta_clicks], ["Certificates F / P / S", `${s.certificates.foundation} / ${s.certificates.practitioner} / ${s.certificates.strategist}`]].map(([k, v]) => (
                <div key={k}><dt className="text-xs text-muted-foreground">{k}</dt><dd className="font-mono text-lg">{v}</dd></div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Admin() {
  return (
    <PageShell wide>
      <div className="overline-label">White-label admin</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">Editions & learners</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Partners can change branding, logo, theme, CTA, issuer and glossary overrides. Core lessons, answer keys, scoring rules and safety language are versioned and locked.</p>
      <Tabs defaultValue="editions" className="mt-8">
        <TabsList className="h-auto max-w-full justify-start overflow-x-auto rounded-sm">
          <TabsTrigger value="editions" data-testid="admin-tab-editions" className="rounded-sm">Editions</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm">Users & access</TabsTrigger>
          <TabsTrigger value="learners" data-testid="admin-tab-learners" className="rounded-sm">Learner progress</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-sm">Analytics</TabsTrigger>
          <TabsTrigger value="executive" data-testid="admin-tab-executive" className="rounded-sm">Executive content</TabsTrigger>
          <TabsTrigger value="videos" data-testid="admin-tab-videos" className="rounded-sm">Lesson videos</TabsTrigger>
        </TabsList>
        <TabsContent value="editions" className="mt-6"><EditionsTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="learners" className="mt-6"><LearnersTab /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
        <TabsContent value="executive" className="mt-6"><ExecutiveReviewTab /></TabsContent>
        <TabsContent value="videos" className="mt-6"><VideosTab /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
